import NiceModal from '@ebay/nice-modal-react';

import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Loading from 'components/global/Loading';
import Menu from 'components/global/Menu';
import PageHeader from 'components/global/PageHeader';
import Tabs from 'components/global/Tabs';

import useAccessControl from 'hooks/useAccessControl';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';

import ImportDataModal from 'modals/ImportDataModal';

import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import { ORDERS } from 'services/consts';

import { setListMapView } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';
import { faDownload, faList, faLocationDot, faPlus } from '@fortawesome/pro-solid-svg-icons';

import { TabTitile } from './Shipments.styled';

/**
 * @author Burak
 */

const Shipments = () => {
	const location = useLocation();
	const { showModal } = useModalRouter();
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const { t } = useTranslation();
	const { pathname } = useLocation();

	const [optionMenuRef, setOptionMenuRef] = useState(null);

	const listMapView = useSelector((state) => state.layout.listMapView);

	const [orderNumbers, setOrderNumbers] = useState({});
	const [orderNumbersLoading, setOrderNumbersLoading] = useState(false);
	const axios = useAxiosPrivate();

	const isMobile = useMediaQuery(Breakpoints.mobile);

	useEffect(() => {
		const fetchOrderNumbers = async () => {
			try {
				setOrderNumbersLoading(true);
				const response = await axios.post(ORDERS.GET_SHIPPING_ORDER_COUNTS, {});
				if (response.status !== 200) {
					AppLogger.error(response);
				}
				setOrderNumbers(response.data);
			} catch (error) {
				AppLogger.error(error);
				setOrderNumbers({});
			} finally {
				setOrderNumbersLoading(false);
			}
		};

		fetchOrderNumbers();
	}, [pathname]);

	const pillColor = useCallback(
		(currentLocation) => {
			if (currentLocation === pathname) {
				if (isMobile) {
					return 'black';
				}
				return 'blue';
			}
			return 'light';
		},
		[isMobile, pathname]
	);

	const tabs = useMemo(
		() => [
			{
				label: 'pendingOrders',
				path: mergePaths(PATHS.SHIPMENTS, PATHS.PENDING_ORDERS)
			},
			{
				label: (
					<TabTitile>
						<div>{t('awaitingPickup')}</div>
						<div>
							{orderNumbersLoading ? (
								<Icon
									color={'var(--blue)'}
									size={'medium'}
									icon={faCircleNotch}
									className="button-icon fa-spin"
								/>
							) : (
								orderNumbers?.awaitingPickupOrders !== undefined && (
									<Badge
										variant="pill"
										size="small"
										color={pillColor(mergePaths(PATHS.SHIPMENTS, PATHS.AWAITING_PICKUP))}
										text={orderNumbers?.awaitingPickupOrders || '0'}
									/>
								)
							)}
						</div>
					</TabTitile>
				),
				path: mergePaths(PATHS.SHIPMENTS, PATHS.AWAITING_PICKUP)
			},
			{
				label: (
					<TabTitile>
						<div>{t('currentlyShipping')}</div>
						<div>
							{orderNumbersLoading ? (
								<Icon
									color={'var(--blue)'}
									size={'medium'}
									icon={faCircleNotch}
									className="button-icon fa-spin"
								/>
							) : (
								orderNumbers?.shippingOrders !== undefined && (
									<Badge
										variant="pill"
										size="small"
										color={pillColor(mergePaths(PATHS.SHIPMENTS, PATHS.CURRENTLY_SHIPPING))}
										text={orderNumbers?.shippingOrders || '0'}
									/>
								)
							)}
						</div>
					</TabTitile>
				),
				path: mergePaths(PATHS.SHIPMENTS, PATHS.CURRENTLY_SHIPPING)
			},
			{
				label: (
					<TabTitile>
						<div>{t('shipmentOnHold')}</div>
						<div>
							{orderNumbersLoading ? (
								<Icon
									color={'var(--blue)'}
									size={'medium'}
									icon={faCircleNotch}
									className="button-icon fa-spin"
								/>
							) : (
								orderNumbers?.onholdOrders !== undefined && (
									<Badge
										variant="pill"
										size="small"
										color={pillColor(mergePaths(PATHS.SHIPMENTS, PATHS.ONHOLD_ORDERS))}
										text={orderNumbers?.onholdOrders || '0'}
									/>
								)
							)}
						</div>
					</TabTitile>
				),
				path: mergePaths(PATHS.SHIPMENTS, PATHS.ONHOLD_ORDERS)
			},
			{
				label: 'delivered',
				path: mergePaths(PATHS.SHIPMENTS, PATHS.DELIVERED_ORDERS)
			},
			{
				label: 'returned',
				path: mergePaths(PATHS.SHIPMENTS, PATHS.RETURNED_ORDERS)
			},
			{
				label: 'canceled',
				path: mergePaths(PATHS.SHIPMENTS, PATHS.CANCELED_ORDERS)
			},
			{
				label: 'allOrders',
				path: mergePaths(PATHS.SHIPMENTS, PATHS.ALL_ORDERS)
			}
		],
		[orderNumbers, orderNumbersLoading, t, isMobile]
	);

	const uploadButtonOptionMenu = useMemo(() => {
		const menu = [
			{
				label: 'orderImport',
				isAuth: true,
				action: () => NiceModal.show(ImportDataModal, importActions.orderImport)
			},
			{
				label: 'sallaOrdersImport',
				isAuth: isAuthorized('/import-export/button/sallaOrdersImport'),
				action: () => NiceModal.show(ImportDataModal, importActions.sallaOrdersImport)
			},
			{
				label: 'ksaOrdersImport',
				isAuth: isAuthorized('/import-export/button/ksaOrdersImport'),
				action: () => NiceModal.show(ImportDataModal, importActions.ksaOrdersImport)
			},
			{
				label: 'customOrdersImport',
				isAuth: isAuthorized('/import-export/button/customOrdersImport'),
				action: () => NiceModal.show(ImportDataModal, importActions.customOrdersImport)
			}
		];
		return menu.filter((item) => item.isAuth);
	}, []);

	return (
		<div>
			<PageHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/shipments-icon-new.json.gz"
				title="shipments">
				{![
					mergePaths(PATHS.SHIPMENTS, PATHS.ALL_ORDERS),
					mergePaths(PATHS.SHIPMENTS, PATHS.DELIVERED_ORDERS)
				].includes(location.pathname) && (
					<Button
						icon={listMapView ? faList : faLocationDot}
						label={listMapView ? 'listView' : 'Mapview'}
						onClick={() => dispatch(setListMapView(!listMapView))}
						variant="borderless"
						lordIcon={
							listMapView
								? 'https://storage.googleapis.com/tryoto-public/lottie-files-compressed/listView-icon.json.gz'
								: 'https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/mapView-icon.json.gz'
						}
						className="mobile-hidden mapViewButton"
					/>
				)}
				<Authorization path="/import-export/button/orderImport">
					<Button
						ref={setOptionMenuRef}
						icon={faDownload}
						variant="borderless"
						label="uploadShipment"
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
						onClick={() =>
							uploadButtonOptionMenu.length === 1 &&
							NiceModal.show(ImportDataModal, importActions.orderImport)
						}
						className="mobile-hidden importDataButton"
					/>

					{uploadButtonOptionMenu.length > 1 && optionMenuRef && (
						<Menu
							placement="bottom-start"
							buttonRef={optionMenuRef}
							hoverMode={false}
							menuItems={uploadButtonOptionMenu}
						/>
					)}
				</Authorization>
				<Authorization path="/modal/add-order-form">
					<Button
						icon={faPlus}
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						label="addOrder"
						onClick={() => showModal('add-order-form')}
						variant="blue"
						className="addButton"
					/>
				</Authorization>
			</PageHeader>
			<Tabs data={tabs} checkURL />
			<Suspense fallback={<Loading />}>
				<Outlet />
			</Suspense>
		</div>
	);
};

export default Shipments;
